import { type ContactCandidate } from '#app/routes/mocra+/_form+/components/contacts/types.ts'

export function combineCandidateLists(
	directoryCandidates: readonly ContactCandidate[],
	provided?: readonly ContactCandidate[],
): ContactCandidate[] {
	const map = new Map<string, ContactCandidate>()

	const addCandidate = (candidate: ContactCandidate | undefined) => {
		if (!candidate) return
		const normalizedEmail = candidate.email?.trim().toLowerCase()
		const normalizedId = candidate.id?.trim()
		if (!normalizedEmail && !normalizedId) return
		const key = normalizedEmail
			? `email:${normalizedEmail}`
			: `id:${normalizedId}`
		const existing = map.get(key)
		if (!existing) {
			map.set(key, normalizeCandidate(candidate))
			return
		}
		map.set(key, mergeCandidates(existing, candidate))
	}

	for (const candidate of directoryCandidates) {
		addCandidate(candidate)
	}

	if (provided) {
		for (const candidate of provided) {
			addCandidate(candidate)
		}
	}

	return Array.from(map.values()).sort((a, b) =>
		(a.name ?? '').localeCompare(b.name ?? '', undefined, {
			sensitivity: 'base',
		}),
	)
}

export function createCandidateSignature(candidate: ContactCandidate): string {
	const id = candidate.id?.trim() ?? ''
	const email = candidate.email?.trim().toLowerCase() ?? ''
	const name = candidate.name?.trim() ?? ''
	const phone = candidate.phone?.trim() ?? ''
	const source = candidate.source?.trim() ?? ''
	return [id, email, name, phone, source].join('|')
}

function normalizeCandidate(candidate: ContactCandidate): ContactCandidate {
	return {
		id: candidate.id?.trim(),
		name: candidate.name?.trim(),
		email: candidate.email?.trim(),
		phone: candidate.phone?.trim(),
		source: candidate.source?.trim(),
	}
}

function mergeCandidates(
	existing: ContactCandidate,
	incoming: ContactCandidate,
): ContactCandidate {
	const normalizedIncoming = normalizeCandidate(incoming)
	const name = pickPreferredValue(
		existing.name,
		normalizedIncoming.name,
		(value) => value?.length ?? 0,
	)
	const phone =
		existing.phone && existing.phone.length > 0
			? existing.phone
			: normalizedIncoming.phone
	const source = pickPreferredValue(
		existing.source,
		normalizedIncoming.source,
		(value) => value?.length ?? 0,
	)

	return {
		id: existing.id ?? normalizedIncoming.id,
		name,
		email: existing.email ?? normalizedIncoming.email,
		phone,
		source,
	}
}

function pickPreferredValue<T extends string | undefined>(
	existing: T,
	incoming: T,
	score: (value: T) => number,
): T {
	const existingScore = score(existing)
	const incomingScore = score(incoming)
	if (incomingScore > existingScore) {
		return incoming
	}
	return existing
}
